import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  usePlans,
  useGoals,
  useActivities,
  useCreatePlan,
} from "@/hooks/useCareer";
import { Modal } from "@/components/ui/modal";
import {
  EntityRow,
  FilterChips,
  StatusChip,
  fieldClassName,
} from "@/components/goals/goals-ui";
import { isOpenWork, suggestedProgressFromWork } from "@/lib/goals-progress";

type PlanFilter = "active" | "paused" | "done" | "all";

export function PlansListPage() {
  const navigate = useNavigate();
  const plansQuery = usePlans();
  const goalsQuery = useGoals();
  const activitiesQuery = useActivities(false);
  const createPlan = useCreatePlan();

  const [filter, setFilter] = useState<PlanFilter>("active");
  const [createOpen, setCreateOpen] = useState(false);
  const [title, setTitle] = useState("");
  const [objective, setObjective] = useState("");
  const [kind, setKind] = useState("search");
  const [goalId, setGoalId] = useState("");

  const plans = plansQuery.data?.plans ?? [];
  const goals = goalsQuery.data?.goals ?? [];
  const activities = activitiesQuery.data?.activities ?? [];
  const goalTitle = (id?: string) =>
    goals.find((g) => g.id === id)?.title ?? null;

  const filtered = useMemo(() => {
    return plans.filter((p) => {
      if (filter === "all") return true;
      if (filter === "active") return p.status === "active";
      if (filter === "paused") return p.status === "paused";
      return p.status === "completed" || p.status === "abandoned";
    });
  }, [plans, filter]);

  async function handleCreate(e: React.FormEvent) {
    e.preventDefault();
    if (!title.trim() || !objective.trim()) return;
    const { plan } = await createPlan.mutateAsync({
      title: title.trim(),
      primaryObjective: objective.trim(),
      kind,
      goalId: goalId || undefined,
    });
    setCreateOpen(false);
    setTitle("");
    setObjective("");
    navigate(`/plans/${plan.id}`);
  }

  return (
    <div className="pb-8">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <FilterChips
          value={filter}
          onChange={setFilter}
          options={[
            { value: "active", label: "Active" },
            { value: "paused", label: "Paused" },
            { value: "done", label: "Done" },
            { value: "all", label: "All" },
          ]}
        />
        <button
          type="button"
          onClick={() => setCreateOpen(true)}
          className="rounded-md bg-brand px-3 py-1.5 text-sm font-medium text-brand-foreground"
        >
          New plan
        </button>
      </div>

      {plansQuery.isLoading ? (
        <p className="text-sm text-muted-foreground">Loading plans…</p>
      ) : filtered.length === 0 ? (
        <div className="rounded-lg border border-dashed border-border px-4 py-10 text-center">
          <p className="text-sm text-muted-foreground">No plans in this filter.</p>
          <button
            type="button"
            onClick={() => setCreateOpen(true)}
            className="mt-3 text-sm text-brand hover:underline"
          >
            Create a plan
          </button>
        </div>
      ) : (
        <ul className="space-y-2">
          {filtered.map((p) => {
            const work = activities.filter((a) => a.planId === p.id);
            const openWork = work.filter((a) => isOpenWork(a.state)).length;
            const suggested = suggestedProgressFromWork(work);
            const parent = goalTitle(p.goalId);
            return (
              <EntityRow
                key={p.id}
                to={`/plans/${p.id}`}
                title={p.title}
                meta={
                  <span className="flex flex-wrap items-center gap-2">
                    <StatusChip>{p.kind}</StatusChip>
                    <span>{p.status}</span>
                    {p.timeHorizon ? <span>{p.timeHorizon}</span> : null}
                    {parent ? <span>→ {parent}</span> : null}
                    <span>
                      {openWork} open work
                      {suggested != null ? ` · ~${suggested}%` : ""}
                    </span>
                  </span>
                }
              />
            );
          })}
        </ul>
      )}

      <Modal open={createOpen} onClose={() => setCreateOpen(false)} labelledBy="new-plan-title">
        <form onSubmit={(e) => void handleCreate(e)} className="flex flex-col">
          <div className="border-b border-border px-4 py-3">
            <h2 id="new-plan-title" className="text-base font-semibold">
              New plan
            </h2>
          </div>
          <div className="space-y-3 overflow-y-auto px-4 py-4">
            <input
              className={fieldClassName()}
              placeholder="Plan title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              autoFocus
              required
            />
            <textarea
              className={fieldClassName()}
              placeholder="Primary objective"
              rows={3}
              value={objective}
              onChange={(e) => setObjective(e.target.value)}
              required
            />
            <select
              className={fieldClassName()}
              value={kind}
              onChange={(e) => setKind(e.target.value)}
            >
              <option value="search">Search</option>
              <option value="networking">Networking</option>
              <option value="negotiation">Negotiation</option>
              <option value="direction">Direction</option>
              <option value="onboarding">Onboarding</option>
            </select>
            <select
              className={fieldClassName()}
              value={goalId}
              onChange={(e) => setGoalId(e.target.value)}
            >
              <option value="">No linked goal</option>
              {goals.map((g) => (
                <option key={g.id} value={g.id}>
                  {g.title}
                </option>
              ))}
            </select>
          </div>
          <div className="flex justify-end gap-2 border-t border-border px-4 py-3">
            <button
              type="button"
              onClick={() => setCreateOpen(false)}
              className="rounded-md px-3 py-1.5 text-sm text-muted-foreground"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={createPlan.isPending}
              className="rounded-md bg-brand px-3 py-1.5 text-sm font-medium text-brand-foreground disabled:opacity-50"
            >
              Create
            </button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
